import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ClientsService } from "../../routes/services/clients.service";
import { FarmService } from "../../routes/services/farm.service";
import { PondService } from "../../routes/services/pond.service";
import { StorageService } from "../../routes/services/storage.service";
import { FeedersService } from "../../routes/services/feeder.service";
import { WaterwiseService } from "../../routes/services/waterwise.service";
import { BrandsService } from "../../routes/Admin/feeders/services/brands.service";
import { DispenserService } from "../../routes/Admin/feeders/services/dispenser.service";
import { GlobalFunction } from "../../routes/services/globalFuntion";

/** Tipos de dispositivo aceptados por el formulario de actualización masiva. */
export type DeviceUpdateType = "feeder" | "waterwise";

@Component({
  selector: "app-update-device-dynamic",
  templateUrl: "./update-device-dynamic.component.html",
  styleUrls: ["./update-device-dynamic.component.scss"]
})
export class UpdateDeviceDynamicComponent implements OnInit, OnDestroy {
  /** Tipo de dispositivo: determina campos y endpoint usado. */
  @Input("deviceType") deviceType: DeviceUpdateType = "feeder";
  /** Lista de dispositivos seleccionados para la actualización masiva. */
  @Input("selected") selected: any[] = [];

  /** Emite la respuesta del servidor tras una petición exitosa. */
  @Output() successPetition = new EventEmitter<any>();

  /** Opciones de tipo de dispositivo waterwise según el backend. */
  readonly deviceTypeOptions = [
    { value: 0, label: "update-device-dynamic.device-type-drone" },
    { value: 1, label: "update-device-dynamic.device-type-station" },
    { value: 2, label: "update-device-dynamic.device-type-buoy" }
  ];

  form: FormGroup = new FormGroup({
    client: new FormControl(""),
    farm: new FormControl(""),
    pond: new FormControl(""),
    brand: new FormControl(""),
    dispenser: new FormControl(""),
    device_type: new FormControl(""),
    warranty_months: new FormControl(""),
    warranty_start_date: new FormControl("")
  });

  clients: any[] = [];
  farms: any[] = [];
  ponds: any[] = [];
  brands: any[] = [];
  dispensers: any[] = [];

  loadingClients = false;
  loadingFarms = false;
  loadingPonds = false;
  loadingBrands = false;
  loadingDispensers = false;
  loading = false;

  private destroy$ = new Subject<void>();

  constructor(
    public storageService: StorageService,
    private clientsService: ClientsService,
    private farmService: FarmService,
    private pondService: PondService,
    private feedersService: FeedersService,
    private waterwiseService: WaterwiseService,
    private brandsService: BrandsService,
    private dispenserService: DispenserService,
    private globalFunction: GlobalFunction
  ) {}

  ngOnInit(): void {
    this.getClients();
    if (this.deviceType === "feeder") {
      this.getBrands();
      this.getDispensers();
    }
    if (this.deviceType === "waterwise") {
      const clientId = this.storageService.getClientID();
      if (clientId) {
        this.getFarms(clientId);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga la lista de clientes para el selector.
   */
  getClients(): void {
    this.loadingClients = true;
    this.clientsService
      .getClients("?fields=id,name")
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.loadingClients = false;
        this.clients = resp;
      });
  }

  /**
   * Carga las granjas del cliente seleccionado.
   * @param clientId ID del cliente.
   */
  getFarms(clientId: any): void {
    if (!clientId) {
      this.farms = [];
      this.ponds = [];
      this.form.patchValue({ farm: "", pond: "" });
      return;
    }
    this.loadingFarms = true;
    this.form.patchValue({ client: clientId });
    this.farmService
      .getFarms(`?fields=id,name&client=${clientId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.loadingFarms = false;
        this.farms = resp;
      });
  }

  /**
   * Carga los estanques de la granja seleccionada.
   * @param farmId ID de la granja.
   */
  getPonds(farmId: any): void {
    if (!farmId) {
      this.ponds = [];
      this.form.patchValue({ pond: "" });
      return;
    }
    this.loadingPonds = true;
    const { alert_query, name_query } = this.storageService.getQueryFilters({
      alert_query: "",
      name_query: ""
    });
    const alert = alert_query ? "-alert_count," : "";
    const name = name_query ? "name_advance" : "number";
    const query = this.globalFunction.constructQuery({
      farm_id: farmId,
      fields: ["id", "name"],
      ordering: alert + name,
      all_records: "true"
    });
    this.pondService
      .getPonds(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.loadingPonds = false;
        this.ponds = resp;
        this.form.patchValue({ farm: farmId });
      });
  }

  /**
   * Carga las marcas disponibles (solo feeders).
   */
  getBrands(): void {
    this.loadingBrands = true;
    this.brandsService
      .listBrands("")
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.loadingBrands = false;
        this.brands = resp;
      });
  }

  /**
   * Carga los dispensadores disponibles (solo feeders).
   */
  getDispensers(): void {
    this.loadingDispensers = true;
    this.dispenserService
      .listDispensers("")
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.loadingDispensers = false;
        this.dispensers = resp;
      });
  }

  /**
   * Construye el objeto de payload incluyendo solo los campos con valor.
   * @returns Objeto con los campos a actualizar.
   */
  private buildPayload(): Record<string, any> {
    const v = this.form.value;
    const obj: Record<string, any> = {};

    if (v.client) obj.client = Number(v.client);
    if (v.farm) obj.farm = Number(v.farm);
    if (v.pond) obj.pond = Number(v.pond);
    if (v.warranty_months !== "" && v.warranty_months !== null) {
      obj.warranty_months = Number(v.warranty_months);
    }
    if (v.warranty_start_date) obj.warranty_start_date = v.warranty_start_date;

    if (this.deviceType === "feeder") {
      if (v.brand) obj.brand = Number(v.brand);
      if (v.dispenser) obj.dispenser = Number(v.dispenser);
    }

    if (this.deviceType === "waterwise") {
      if (v.device_type !== "" && v.device_type !== null) {
        obj.device_type = Number(v.device_type);
      }
    }

    return obj;
  }

  /**
   * Envía la actualización masiva al backend.
   */
  register(): void {
    if (this.loading) return;
    const query = `?id_in=${this.selected.map((item) => item.id).join(",")}`;
    const payload = this.buildPayload();
    this.loading = true;

    if (this.deviceType === "feeder") {
      this.feedersService
        .EditMasiveFeeders(query, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe((resp) => {
          this.loading = false;
          this.successPetition.emit(resp);
        });
    } else {
      this.waterwiseService
        .multipleUpdateWaterwise(query, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe((resp) => {
          this.loading = false;
          this.successPetition.emit(resp);
        });
    }
  }
}
